import type {
  ChatbotConfig,
  EscalateContact,
  EscalateResponse,
  UIMessage,
  WhatsappCheckResponse,
} from './types'

export async function fetchConfig(
  origin: string,
  productSlug: string,
): Promise<ChatbotConfig | null> {
  try {
    const res = await fetch(`${origin}/api/public/chatbot/config/${encodeURIComponent(productSlug)}`, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'omit',
    })
    if (!res.ok) return null
    const data = await res.json() as ChatbotConfig
    return data
  }
  catch {
    return null
  }
}

export interface StreamMessageInput {
  origin: string
  productSlug: string
  sessionId: string
  messages: UIMessage[]
  pageUrl?: string
  user?: { email?: string, name?: string } | null
}

export interface StreamErrorChunk {
  type: 'error'
  message: string
}

export interface StreamTextChunk {
  type: 'text'
  text: string
}

export type StreamChunk = StreamTextChunk | StreamErrorChunk

// Never surface a raw status/JSON blob to a visitor. Map to a calm es-AR
// message that always points to a human when the bot can't respond.
const HUMAN_HINT = 'Tocá "Hablar con persona" arriba y el equipo te ayuda.'
function friendlyError(status: number): string {
  if (status === 429 || status === 402) {
    return `El asistente está con mucha demanda en este momento. ${HUMAN_HINT}`
  }
  if (status === 413) {
    return `Ese mensaje es muy largo. Probá resumirlo, o ${HUMAN_HINT.charAt(0).toLowerCase()}${HUMAN_HINT.slice(1)}`
  }
  return `No pude responder en este momento. ${HUMAN_HINT}`
}

/**
 * Stream assistant response as plain text chunks.
 *
 * The backend is configured to use AI SDK's `toTextStreamResponse()` which
 * emits the raw assistant text as a ReadableStream<Uint8Array>. We decode,
 * yield text chunks, and surface errors via the `error` sentinel so the
 * caller can render a visible error state.
 */
export async function* streamMessage(
  input: StreamMessageInput,
): AsyncGenerator<StreamChunk, void, void> {
  let res: Response
  try {
    res = await fetch(`${input.origin}/api/chatbot/message`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'accept': 'text/plain, text/event-stream',
      },
      credentials: 'omit',
      body: JSON.stringify({
        productSlug: input.productSlug,
        sessionId: input.sessionId,
        messages: input.messages,
        pageUrl: input.pageUrl,
        user: input.user ?? undefined,
      }),
    })
  }
  catch (err) {
    yield { type: 'error', message: err instanceof Error ? err.message : 'Network error' }
    return
  }

  if (!res.ok || !res.body) {
    yield { type: 'error', message: friendlyError(res.status) }
    return
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      if (value && value.byteLength > 0) {
        const text = decoder.decode(value, { stream: true })
        if (text.length > 0) yield { type: 'text', text }
      }
    }
    const tail = decoder.decode()
    if (tail.length > 0) yield { type: 'text', text: tail }
  }
  catch (err) {
    yield { type: 'error', message: err instanceof Error ? err.message : 'Stream error' }
  }
}

export interface WhatsappCheckInput {
  origin: string
  productSlug: string
  sessionId: string
}

/**
 * Fails open: if our own check endpoint is unreachable, that's our infra
 * problem, not a reason to block someone from reaching a person on WhatsApp.
 */
export async function checkWhatsapp(input: WhatsappCheckInput): Promise<WhatsappCheckResponse> {
  try {
    const res = await fetch(`${input.origin}/api/public/chatbot/whatsapp-check`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({
        productSlug: input.productSlug,
        sessionId: input.sessionId,
      }),
    })
    if (!res.ok) return { allowed: true }
    return await res.json() as WhatsappCheckResponse
  }
  catch {
    return { allowed: true }
  }
}

export interface EscalateInput {
  origin: string
  productSlug: string
  sessionId: string
  contact: EscalateContact
  subject?: string
}

export async function escalate(input: EscalateInput): Promise<EscalateResponse> {
  const res = await fetch(`${input.origin}/api/chatbot/escalate`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'omit',
    body: JSON.stringify({
      productSlug: input.productSlug,
      sessionId: input.sessionId,
      contact: input.contact,
      subject: input.subject,
    }),
  })
  if (!res.ok) {
    let detail = `HTTP ${res.status}`
    try {
      const text = await res.text()
      if (text) detail = text.slice(0, 200)
    }
    catch {}
    throw new Error(detail)
  }
  return await res.json() as EscalateResponse
}
